import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Chip,
} from '@material-ui/core';

interface InputRendererProps {
  inputs: Record<string, any>;
}

function formatTagValue(_key: string, value: any): string {
  if (Array.isArray(value)) {
    return value.join(' / ');
  }
  return String(value ?? '');
}

function AwsAccountInputRenderer({ inputs }: InputRendererProps): JSX.Element {
  const rows = [
    ['Account Name', inputs.accountName],
    ['Enterprise Support', inputs.enableEnterpriseSupport ? 'Yes' : 'No'],
    ['Environment Type', inputs.envType],
    ['Approver Set', inputs.approverSet?.setName],
  ];

  return (
    <>
      <Typography variant="subtitle2" gutterBottom>AWS Account Details</Typography>
      <Table size="small">
        <TableBody>
          {rows.map(([label, value]) => (
            <TableRow key={label as string}>
              <TableCell variant="head" style={{ width: '40%' }}>{label}</TableCell>
              <TableCell>{String(value ?? '')}</TableCell>
            </TableRow>
          ))}
          {inputs.approverSet?.isNew && inputs.approverSet?.owners?.length > 0 && (
            <TableRow>
              <TableCell variant="head">New Set Owners</TableCell>
              <TableCell>
                {inputs.approverSet.owners.map((o: string) => (
                  <Chip key={o} label={o} size="small" style={{ marginRight: 4 }} />
                ))}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {inputs.tags && (
        <>
          <Typography variant="subtitle2" gutterBottom style={{ marginTop: 12 }}>Tags</Typography>
          <Table size="small">
            <TableBody>
              {Object.entries(inputs.tags).map(([key, value]) => (
                <TableRow key={key}>
                  <TableCell variant="head" style={{ width: '40%' }}>{key}</TableCell>
                  <TableCell>{formatTagValue(key, value)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </>
  );
}

function GenericJsonInputRenderer({ inputs }: InputRendererProps): JSX.Element {
  return (
    <pre style={{ fontSize: '0.85em', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {JSON.stringify(inputs, null, 2)}
    </pre>
  );
}

const RENDERERS: Record<string, React.ComponentType<InputRendererProps>> = {
  'aws-account': AwsAccountInputRenderer,
};

export function InputRenderer({ kind, inputs }: { kind: string; inputs: Record<string, any> }): JSX.Element {
  const Renderer = RENDERERS[kind] ?? GenericJsonInputRenderer;
  return <Renderer inputs={inputs} />;
}
